import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import GoalTypeDetails from './GoalTypeDetails';

// Task 27 — "Dönem Sonu" sekmesi yönetici KR puanlama formu. GET
// v1/period-goals/{id} ile hedef detayı (KR'ler + çalışanın zaten girdiği
// EmployeeScore) çekilir; yönetici her aktif KR için 1-5 ManagerScore + yorum
// girer. Backend sıra kuralı: hedefin AKTİF KR'larının TÜMÜNDE EmployeeScore
// dolu olmalı, aksi halde "performance.goal.employeeScoresFirst" (409) —
// gereksiz başarısız istek atmamak için submit, eksik employeeScore varken FE
// tarafında da devre dışı bırakılır (GoalEditor'ün canApproveClosure deseniyle
// aynı önleyici yaklaşım). KPI hedeflerde KR yok — bu form yalnız OKR içindir;
// KPI'da yönetici puanlaması yoktur (yalnız çalışanın gerçekleşen değeri +
// kendi öz değerlendirmesi salt-okunur gösterilir).
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 720px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 10px;
  border-bottom: 1px solid #E8ECF0;
  padding-bottom: 6px;
`;

const ReadonlyValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 4px 0;
`;

const KrRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 2fr;
  gap: 10px;
  align-items: center;
  background: #F5F7FA;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const HintText = styled.div`
  font-size: 12px;
  color: #b02a37;
  margin-top: 4px;
`;

const EmptyBox = styled.div`
  padding: 20px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

// 1-5 puan seçenekleri — ManagerRatingPage'deki SCORE_OPTIONS deseninin aynısı
// (serbest sayı girişi yerine select: aralık dışı değer girilemez).
const SCORE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ id: n, name: String(n) }));

export default function PeriodEndScoringModal({
  open, goalId, onClose, onScored, getById, submitManagerScores,
  // PMF-11 — KPI dalının gönderme yolu. Sayfa geçirmezse KPI formu salt-okunur
  // kalır (eski davranış) — bu yüzden kablolama AYRI bir testle çapalandı:
  // prop'u geçirmeyi unutmak hiçbir davranış testini kırmazdı.
  submitTeamFinalKpi,
  // P3 (2026-10-08) — kilometre taşı adım kararı ve eşik/ikili final sonuç (yönetici).
  decideMilestoneStep,
  submitTeamFinalThreshold,
}) {
  const { t } = useTranslation();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scoresDraft, setScoresDraft] = useState({});
  const [commentsDraft, setCommentsDraft] = useState({});
  const [kpiActualDraft, setKpiActualDraft] = useState('');
  // P3 — eşik sonucu taslağı: sayısal → value, ikili → 'yes'|'no'.
  const [thresholdDraft, setThresholdDraft] = useState({ value: '', met: '' });
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  // Plan 3, Task 3 kapı-3 (performance.goal.weightSumInvalid, 409) — config/index.js'in
  // submitManagerScores'u artık {ok, gateError} döner (bkz. o dosyadaki yorum).
  // İnterceptor toast'u zaten anahtar metnini basıyor; bu MODAL İÇİ satır brief'in
  // istediği EK currentSum bağlamı içindir ("toast DEĞİL, mevcut modal hata desenine
  // bak" — missingEmployeeScores'ın HintText deseninin AYNISI).
  const [gateError, setGateError] = useState(null);

  useEffect(() => {
    if (!open || !goalId) { setGoal(null); setGateError(null); return; }
    setLoading(true);
    setGateError(null);
    getById(goalId).then((g) => {
      setGoal(g);
      const scores = {};
      const comments = {};
      (g?.keyResults || []).forEach((kr) => {
        scores[kr.id] = kr.managerScore ?? null;
        comments[kr.id] = kr.managerComment || '';
      });
      setScoresDraft(scores);
      setCommentsDraft(comments);
      // PMF-11 — KPI gerçekleşen değeri kaydedilmiş haliyle açılır (GoalEditor'ün
      // kpiActualDraft deseniyle aynı): yönetici önceki girişini görmeden üstüne
      // yazmasın.
      setKpiActualDraft(g?.kpiActualValue != null ? String(g.kpiActualValue) : '');
      setThresholdDraft({
        value: g?.thresholdActualValue != null ? String(g.thresholdActualValue) : '',
        met: g?.thresholdMet == null ? '' : (g.thresholdMet ? 'yes' : 'no'),
      });
    }).finally(() => setLoading(false));
  }, [open, goalId, getById, reloadKey]);

  // P3 — adım kararından sonra hedefi tazele (ilerleme/durum değişir).
  const reloadGoal = useCallback(() => setReloadKey((k) => k + 1), []);

  const isBinary = goal?.thresholdOperator === 'binary';
  const canSubmitThreshold = !!goal && goal.goalType === 'threshold' && !!submitTeamFinalThreshold
    && (isBinary ? thresholdDraft.met !== '' : (thresholdDraft.value !== '' && !Number.isNaN(Number(thresholdDraft.value))));

  const handleSubmitThreshold = useCallback(async () => {
    if (!canSubmitThreshold) return;
    setBusy(true);
    const payload = isBinary ? { met: thresholdDraft.met === 'yes' } : { value: Number(thresholdDraft.value) };
    const result = await submitTeamFinalThreshold(goal.id, payload);
    setBusy(false);
    if (result?.ok) {
      setGateError(null);
      onScored();
    } else {
      setGateError(result?.gateError || null);
    }
  }, [canSubmitThreshold, isBinary, thresholdDraft, submitTeamFinalThreshold, goal, onScored]);

  const missingEmployeeScores = useMemo(() => {
    if (!goal || goal.goalType !== 'okr') return false;
    return (goal.keyResults || []).some((kr) => kr.employeeScore == null);
  }, [goal]);

  const handleSubmit = useCallback(async () => {
    if (!goal || goal.goalType !== 'okr' || missingEmployeeScores) return;
    const scores = (goal.keyResults || [])
      .filter((kr) => scoresDraft[kr.id] !== '' && scoresDraft[kr.id] != null)
      .map((kr) => ({
        keyResultId: kr.id,
        score: Number(scoresDraft[kr.id]),
        comment: commentsDraft[kr.id] || undefined,
      }));
    setBusy(true);
    const result = await submitManagerScores(goal.id, scores);
    setBusy(false);
    if (result?.ok) {
      setGateError(null);
      onScored();
    } else {
      setGateError(result?.gateError || null);
    }
  }, [goal, missingEmployeeScores, scoresDraft, commentsDraft, submitManagerScores, onScored]);

  // PMF-11 — KPI dalının gönderimi. OKR dalından AYRI tutuldu: farklı uç
  // (kpi-final), farklı ön koşul (employeeScores sırası KPI'da yok — KR yok) ve
  // farklı gövde. Tek bir handle'a sıkıştırmak iki akışı da bulanıklaştırırdı.
  const canSubmitKpi = !!goal
    && goal.goalType === 'kpi'
    && !!submitTeamFinalKpi
    && kpiActualDraft !== ''
    && !Number.isNaN(Number(kpiActualDraft));

  const handleSubmitKpi = useCallback(async () => {
    if (!goal || goal.goalType !== 'kpi' || !submitTeamFinalKpi) return;
    if (kpiActualDraft === '' || Number.isNaN(Number(kpiActualDraft))) return;
    setBusy(true);
    const result = await submitTeamFinalKpi(goal.id, Number(kpiActualDraft));
    setBusy(false);
    if (result?.ok) {
      setGateError(null);
      onScored();
    } else {
      setGateError(result?.gateError || null);
    }
  }, [goal, kpiActualDraft, submitTeamFinalKpi, onScored]);

  if (!open) return null;

  return (
    <Modal>
      <ModalCard>
        <DialogTitle>{t('performance.teamGoals.managerScoring.title')}</DialogTitle>

        {loading && <EmptyBox>{t('common.loading')}</EmptyBox>}

        {!loading && goal && (
          <>
            <ReadonlyValue><strong>{goal.employeeName}</strong> — {goal.title}</ReadonlyValue>

            {/* P3 — kilometre taşı: kabul bekleyen adımlar burada kabul/ret edilir (kanıtla). */}
            {goal.goalType === 'milestone' && (
              <GoalTypeDetails
                goal={goal}
                mode={decideMilestoneStep ? 'manager' : 'readonly'}
                canAct={!!decideMilestoneStep && ['approved', 'evaluating'].includes(goal.status)}
                onDecideStep={decideMilestoneStep}
                onChanged={reloadGoal}
              />
            )}
            {/* P3 — eşik/ikili: koşul + yöneticinin final sonuç girişi (submitTeamFinalKpi ikizi). */}
            {goal.goalType === 'threshold' && (
              <FieldGroup>
                <GoalTypeDetails goal={goal} mode="readonly" />
                {submitTeamFinalThreshold && (isBinary ? (
                  <GenericSelect
                    label={t('performance.goal.threshold.resultLabel')}
                    value={thresholdDraft.met}
                    onChange={(v) => setThresholdDraft((prev) => ({ ...prev, met: v || '' }))}
                    options={[
                      { id: 'yes', name: t('performance.goal.threshold.yes') },
                      { id: 'no', name: t('performance.goal.threshold.no') },
                    ]}
                    idKey="id"
                    labelKey="name"
                  />
                ) : (
                  <TextInput
                    label={t('performance.goal.threshold.actual')}
                    type="number"
                    value={thresholdDraft.value}
                    onChange={(e) => setThresholdDraft((prev) => ({ ...prev, value: e.target.value }))}
                    data-role="threshold-final-input"
                  />
                ))}
                {goal.deviationRationale && (
                  <ReadonlyValue>{t('performance.goal.deviationRationale')}: {goal.deviationRationale}</ReadonlyValue>
                )}
                {gateError && (
                  <HintText data-role="weight-gate-error">
                    {t('performance.teamGoals.managerScoring.weightSumInvalid')}: {gateError.currentSum}/100
                  </HintText>
                )}
              </FieldGroup>
            )}
            {goal.goalType === 'okr' && (
              <>
                <SectionTitle>{t('performance.goal.keyResults')}</SectionTitle>
                {(goal.keyResults || []).map((kr) => (
                  <KrRow key={kr.id}>
                    <ReadonlyValue>{kr.title}</ReadonlyValue>
                    <ReadonlyValue>
                      {t('performance.goal.employeeScore')}: {kr.employeeScore ?? '-'}
                    </ReadonlyValue>
                    <GenericSelect
                      label={t('performance.goal.managerScore')}
                      value={scoresDraft[kr.id] ?? null}
                      onChange={(v) => setScoresDraft((prev) => ({ ...prev, [kr.id]: v }))}
                      options={SCORE_OPTIONS}
                    />
                    <TextInput
                      label={t('performance.teamGoals.managerScoring.comment')}
                      value={commentsDraft[kr.id] ?? ''}
                      onChange={(e) => setCommentsDraft((prev) => ({ ...prev, [kr.id]: e.target.value }))}
                    />
                  </KrRow>
                ))}
                {goal.selfAssessmentText && (
                  <FieldGroup>
                    <ReadonlyValue>
                      <strong>{t('performance.goal.selfAssessment')}:</strong> {goal.selfAssessmentText}
                    </ReadonlyValue>
                  </FieldGroup>
                )}
                {goal.deviationRationale && (
                  <FieldGroup>
                    <ReadonlyValue>
                      <strong>{t('performance.goal.deviationRationale')}:</strong> {goal.deviationRationale}
                    </ReadonlyValue>
                  </FieldGroup>
                )}
                {missingEmployeeScores && (
                  <HintText>{t('performance.teamGoals.managerScoring.waitingEmployeeScores')}</HintText>
                )}
                {/* Plan 3, Task 3 kapı-3 (409 weightSumInvalid) — modal içi hata satırı,
                    missingEmployeeScores'ın AYNI HintText deseni; interceptor toast'u
                    zaten anahtar metnini bastığı için burada EK olarak currentSum
                    gösterilir (bkz. gateError state yorumu). */}
                {gateError && (
                  <HintText data-role="weight-gate-error">
                    {t('performance.teamGoals.managerScoring.weightSumInvalid')}: {gateError.currentSum}/100
                  </HintText>
                )}
              </>
            )}
            {goal.goalType === 'kpi' && (
              <FieldGroup>
                <ReadonlyValue>
                  {t('performance.goal.kpiTargetValue')}: {goal.kpiTargetValue ?? '-'}
                  {goal.completionPct != null ? ` (${t('performance.goal.kpiCompletionPct')}: ${goal.completionPct}%)` : ''}
                </ReadonlyValue>
                {/* PMF-11 — gerçekleşen değer artık DÜZENLENEBİLİR. Sahibi izinli/
                    ayrılmış bir KPI hedefi eskiden hiçbir yerden kapatılamıyordu:
                    backend ucu (POST v1/period-goals/{id}/kpi-final, isManager)
                    vardı ama bu modal salt-okunurdu ve gönder butonu yalnız OKR
                    için çiziliyordu. submitTeamFinalKpi geçirilmediyse eski
                    salt-okunur davranışa düşülür. */}
                {submitTeamFinalKpi ? (
                  <TextInput
                    label={t('performance.goal.kpiActualValue')}
                    type="number"
                    value={kpiActualDraft}
                    onChange={(e) => setKpiActualDraft(e.target.value)}
                    data-role="kpi-actual-input"
                  />
                ) : (
                  <ReadonlyValue>
                    {t('performance.goal.kpiActualValue')}: {goal.kpiActualValue ?? '-'}
                  </ReadonlyValue>
                )}
                {goal.selfAssessmentText && (
                  <ReadonlyValue>{t('performance.goal.selfAssessment')}: {goal.selfAssessmentText}</ReadonlyValue>
                )}
                {goal.deviationRationale && (
                  <ReadonlyValue>{t('performance.goal.deviationRationale')}: {goal.deviationRationale}</ReadonlyValue>
                )}
                {gateError && (
                  <HintText data-role="weight-gate-error">
                    {t('performance.teamGoals.managerScoring.weightSumInvalid')}: {gateError.currentSum}/100
                  </HintText>
                )}
              </FieldGroup>
            )}
          </>
        )}

        <Actions>
          <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
          {goal?.goalType === 'okr' && (
            <Button onClick={handleSubmit} disabled={missingEmployeeScores || busy} loading={busy}>
              {t('performance.teamGoals.managerScoring.save')}
            </Button>
          )}
          {/* PMF-11 — KPI dalının gönder butonu. Eskiden HİÇ YOKTU. */}
          {goal?.goalType === 'kpi' && submitTeamFinalKpi && (
            <Button onClick={handleSubmitKpi} disabled={!canSubmitKpi || busy} loading={busy}>
              {t('performance.teamGoals.managerScoring.save')}
            </Button>
          )}
          {/* P3 — eşik/ikili final sonuç. Kilometre taşında ayrı "kaydet" yok (adım kararları anında). */}
          {goal?.goalType === 'threshold' && submitTeamFinalThreshold && (
            <Button onClick={handleSubmitThreshold} disabled={!canSubmitThreshold || busy} loading={busy} data-action="save-threshold-final">
              {t('performance.teamGoals.managerScoring.save')}
            </Button>
          )}
        </Actions>
      </ModalCard>
    </Modal>
  );
}
